import axios from "plugins/axios";
import Axios from "axios";
import i18n from "plugins/i18n";
import ajaxCancellableToken from "plugins/ajaxCancellableToken";

/**
 * Create mutations object.
 *
 * @param {String} name The mutation name.
 * @return {Object} The mutation object.
 */
function mutations(name) {
    const s = "API_" + name.toUpperCase();
    return {
        pending: s + "_PENDING",
        success: s + "_SUCCESS",
        failure: s + "_FAILURE"
    };
}

/**
 * Ajax request for the store.
 *
 * @param {Object} store The vuex store to use.
 * @param {Object} progress The progress system.
 * @param {String} method The method we want.
 * @param {String} url The url to call.
 * @param {Object | undefined} data The data to pass to the request.
 * @param {Boolean | String | undefined} cancellable If the request is cancellable or not.
 *   If set to true (or if an axio's cancelToken is given, or a string), then everytime this function is called,
 *   The cancel action will take place, trying to clean all previous queries with:
 *     - same mutationName if cancellable was a boolean
 *     - same cancellable string if cancellable was a string
 * @param {Object} mutationName The mutation name to use.
 * @param {object} flashSuccessMessage The success message in case of success query.
 * @param {Object} flashErrorMessage The error message in case of failed query.
 * @param {Object | undefined} data The data.
 */
function ajax(store, method, url, data, mutationName, cancellable, flashSuccessMessage, flashErrorMessage) {
    const states = mutations(mutationName);
    const lMutationName = mutationName.toLowerCase();
    store.commit("setPending", {
        name: lMutationName,
        value: true
    });

    store.commit(states.pending);
    try {
        method = method.toLowerCase();
    } catch (e) {
        method = "get";
    }

    // Handling previous axios requests to cancel
    let cancellableName;
    if (typeof cancellable === "string" || cancellable instanceof String) {
        cancellableName = cancellable;
    } else if (typeof cancellable === "boolean" || cancellable instanceof Boolean) {
        cancellableName = mutationName;
    }
    if (cancellableName) {
        ajaxCancellableToken.cancel(cancellableName);
    }

    let request;
    if (cancellableName) {
        const source = ajaxCancellableToken.generate(cancellableName);
        if (data && method === "delete") {
            request = axios[method](url, {
                data: data
            }, {
                cancelToken: source.token
            });
        } else if (data) {
            request = axios[method](url, data, {
                cancelToken: source.token
            });
        } else {
            request = axios[method](url, {
                cancelToken: source.token
            });
        }
    } else {
        if (data && method === "delete") {
            request = axios[method](url, {
                data: data
            });
        } else if (data) {
            request = axios[method](url, data);
        } else {
            request = axios[method](url);
        }
    }

    return request.then(function(response) {
        store.commit(states.success, response.data);

        if (flashSuccessMessage) {
            if (flashSuccessMessage.autoTranslate === true) {
                try {
                    flashSuccessMessage.message = i18n.t(response.data.message);
                }
                // eslint-disable-next-line
                catch (e) {}
            }
            store.commit("flash/addFlash", flashSuccessMessage, {
                root: true
            });
        }

        store.commit("setPending", {
            name: lMutationName,
            value: false
        });

        return Promise.resolve(response);
    }).catch(function(error) {
        // if it has been cancelled, we do nothing
        if (Axios.isCancel(error)) {
            return Promise.reject(error);
        }

        // If not, it's a "true" error that needs
        // to be shown to user.
        store.commit(states.failure, error);

        if (flashErrorMessage) {
            if (flashErrorMessage.autoTranslate === true) {
                try {
                    flashErrorMessage.message = i18n.t(error.response.data.error.message);
                } catch (e) {
                    flashErrorMessage.message = i18n.t(error.message);
                }
            }

            store.commit("flash/addFlash", flashErrorMessage, {
                root: true
            });
        }

        store.commit("setPending", {
            name: lMutationName,
            value: false
        });

        return Promise.reject(error);
    });
}

export default ajax;